import type { CSSProperties } from 'react';

/** The five avatar pairs from the README (bg / fg). */
export const AVATAR_PAIRS: ReadonlyArray<{ bg: string; fg: string }> = [
  { bg: '#e8e2f5', fg: '#4a3b7a' },
  { bg: '#dde8f5', fg: '#1e3a5f' },
  { bg: '#e3f0e3', fg: '#2f6b3a' },
  { bg: '#fbe9d7', fg: '#8a4b1f' },
  { bg: '#f5e2e8', fg: '#7a2e4a' },
];

/** Pending invitations render grey (the prototype's u6). */
export const AVATAR_PENDING = { bg: '#ececea', fg: '#6b6b70' };

function hash(s: string): number {
  let h = 0;
  for (const ch of s) h = (h * 31 + ch.charCodeAt(0)) % 360;
  return h;
}

/** Stable pair for a person, from their name (or email). */
export function avatarPair(key: string): { bg: string; fg: string } {
  return AVATAR_PAIRS[hash(key) % AVATAR_PAIRS.length];
}

/** The prototype's `initials()`: strips a title, handles an email. */
export function initials(name: string): string {
  const n = (name || '').replace(/^(Dr|Prof|Mr|Mrs|Ms)\.?\s+/i, '');
  if (n.includes('@')) return (n[0] + (n.split(/[.@_-]/)[1] || ' ')[0]).toUpperCase().trim();
  return n.split(' ').filter(Boolean).map((p) => p[0]).join('').slice(0, 2).toUpperCase();
}

const FONT: Record<number, number> = { 22: 9.5, 28: 10, 30: 11, 32: 11, 34: 12, 44: 15, 64: 22 };

/**
 * Initials in a circle. Sizes used by the prototype: 22 (author line),
 * 28 (comments, notifications), 30 (top bar), 32 (people table), 34 (account
 * menu), 44 (account sidebar), 64 (profile).
 */
export function Avatar({
  name,
  size = 28,
  bg,
  fg,
  pending,
  ring,
  style,
  onClick,
}: {
  name: string;
  size?: number;
  bg?: string;
  fg?: string;
  pending?: boolean;
  /** the open-menu ring on the top-bar avatar */
  ring?: boolean;
  style?: CSSProperties;
  onClick?: (e: React.MouseEvent) => void;
}) {
  const pair = pending ? AVATAR_PENDING : avatarPair(name);
  return (
    <div
      onClick={onClick}
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: bg ?? pair.bg,
        color: fg ?? pair.fg,
        display: 'grid',
        placeItems: 'center',
        fontSize: FONT[size] ?? Math.round(size * 0.36),
        fontWeight: 600,
        flex: 'none',
        cursor: onClick ? 'pointer' : undefined,
        boxShadow: ring ? 'var(--tr-ring)' : undefined,
        lineHeight: 1,
        ...style,
      }}
    >
      {initials(name)}
    </div>
  );
}

/**
 * Author Chip.dc.html: a small oklch-tinted circle with white initials, then
 * the name. `size` is the circle (default 18), `fontSize` the name (12).
 */
export function AuthorChip({
  author,
  size = 18,
  fontSize = 12,
  color = '#6b6b70',
  weight = 400,
  bg,
  style,
}: {
  author: string;
  size?: number;
  fontSize?: number;
  color?: string;
  weight?: number;
  bg?: string;
  style?: CSSProperties;
}) {
  const clean = author.replace(/^(Dr|Prof|Mr|Mrs|Ms)\.?\s+/i, '');
  const h = hash(author);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, ...style }}>
      <div
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          background: bg || `oklch(0.55 0.1 ${h})`,
          color: '#fff',
          display: 'grid',
          placeItems: 'center',
          fontSize: Math.round(size * 0.42),
          fontWeight: 600,
          flex: 'none',
          lineHeight: 1,
        }}
      >
        {clean.split(' ').filter(Boolean).map((x) => x[0]).join('').slice(0, 2).toUpperCase()}
      </div>
      <span className="tr-ellipsis" style={{ fontSize, color, fontWeight: weight }}>
        {author}
      </span>
    </div>
  );
}
